/**
 * Shared publication-status presentation for Calendar tiles and the
 * Schedule left shorts list. One canonical mapping (kind → label / colour);
 * each surface adapts density (per-pub icon vs short-level aggregate).
 *
 * The queue shows posting outcomes beside the video title. Future scheduled
 * and newly staged posts have no queue status. The Posts inspector distinguishes
 * persisted scheduling with a date-trailing icon
 * (see `publicationStatusBadge.ts`).
 */

import { isFiringPub, POST_NOW_FIRING_LABEL } from "./postNow";

export type PublicationStatusInput = {
  status: string;
  /** Only supply a scheduled date for a persisted post. */
  scheduledAt?: Date | null;
};

export type PublicationStatusKind =
  | "published"
  | "failed"
  | "queued"
  | "publishing"
  | "verifying";

/** In-flight statuses collapse to one aggregate face. */
export type PublicationStatusAggregateKind =
  | "published"
  | "partially-published"
  | "failed"
  | "queued"
  | "publishing";

export type PublicationStatusPresentation = {
  kind: PublicationStatusKind;
  /** Single-post tooltip / accessible name: "Posted", "Failed", … */
  label: string;
  /** Icon colour class (text-*). */
  iconClassName: string;
};

const STATUS_LABELS: Record<PublicationStatusKind, string> = {
  published: "Posted",
  failed: "Failed",
  queued: POST_NOW_FIRING_LABEL,
  publishing: "Posting",
  verifying: "Verifying",
};

const STATUS_ICON_CLASSES: Record<PublicationStatusKind, string> = {
  // Posted is a fact, pending is provisional: the check is the tile's
  // primary slate (the time's shade) while in-flight glyphs stay one step
  // lighter. No hue: red on failed is the only alarm colour.
  published: "text-slate-700 dark:text-foreground",
  failed: "text-red-500",
  queued: "text-slate-500 dark:text-foreground-secondary",
  publishing: "text-slate-500 dark:text-foreground-secondary",
  verifying: "text-slate-500 dark:text-foreground-secondary",
};

/**
 * Canonical per-publication status kind for icon surfaces. Returns null for
 * future scheduled / newly staged / unscheduled posts — no status icon.
 * Due scheduled posts are queued until the publisher starts processing them.
 */
export const selectPublicationStatusKind = (
  publication: PublicationStatusInput,
  now: Date = new Date(),
): PublicationStatusKind | null => {
  const { status } = publication;
  if (status === "published") return "published";
  if (status === "failed") return "failed";
  if (status === "publishing") return "publishing";
  if (status === "verifying") return "verifying";
  if (isFiringPub(publication, now)) return "queued";
  return null;
};

export const getPublicationStatusPresentation = (
  kind: PublicationStatusKind,
): PublicationStatusPresentation => ({
  kind,
  label: STATUS_LABELS[kind],
  iconClassName: STATUS_ICON_CLASSES[kind],
});

/** Plain-text count shared by tooltips on the short pickers and schedule queue. */
export const getPublishedCountLabel = (
  publications: ReadonlyArray<{ status: string }>,
): string =>
  `${publications.filter((post) => post.status === "published").length} of ${publications.length} posted`;

export type ShortStatusAggregate = {
  kind: PublicationStatusAggregateKind;
  /** Short outcome name; the queue renders an icon instead of this text. */
  label: string;
  /** Concise outcome or completion count for hover and keyboard focus. */
  tooltip: string;
  iconClassName: string;
  postedCount: number;
  totalCount: number;
};

/**
 * One trailing queue status: failure > active posting > queued > partial / completed.
 * Every row retains its queue position in the leading gutter.
 * Counts stay slate-500; the leading check steps up to slate-700 when all posts are published.
 */
export const selectShortStatusAggregate = <
  T extends PublicationStatusInput,
>(
  publications: ReadonlyArray<T>,
  now: Date = new Date(),
): ShortStatusAggregate | null => {
  if (publications.length === 0) return null;

  const count = (status: string) =>
    publications.filter((post) => post.status === status).length;
  const failedCount = count("failed");
  const postedCount = count("published");
  const kinds = publications.map((post) => selectPublicationStatusKind(post, now));
  const postingCount = kinds.filter((kind) => kind === "publishing" || kind === "verifying").length;
  const queuedCount = kinds.filter((kind) => kind === "queued").length;
  if (failedCount === 0 && postedCount === 0 && postingCount === 0 && queuedCount === 0) return null;

  const totalCount = publications.length;
  const details = { postedCount, totalCount };

  if (failedCount > 0) {
    const label = failedCount === totalCount
      ? "All posts failed"
      : `${failedCount} post${failedCount === 1 ? "" : "s"} failed`;
    return {
      ...details,
      kind: "failed",
      label,
      tooltip: label,
      iconClassName: STATUS_ICON_CLASSES.failed,
    };
  }

  if (postingCount > 0) {
    return {
      ...details,
      kind: "publishing",
      label: STATUS_LABELS.publishing,
      tooltip: STATUS_LABELS.publishing,
      iconClassName: STATUS_ICON_CLASSES.publishing,
    };
  }

  if (queuedCount > 0) {
    return {
      ...details,
      kind: "queued",
      label: STATUS_LABELS.queued,
      tooltip: STATUS_LABELS.queued,
      iconClassName: STATUS_ICON_CLASSES.queued,
    };
  }

  if (postedCount === totalCount) {
    return {
      ...details,
      kind: "published",
      label: "Posted",
      tooltip: "Posted",
      iconClassName: STATUS_ICON_CLASSES.published,
    };
  }

  const label = getPublishedCountLabel(publications);
  return {
    ...details,
    kind: "partially-published",
    label,
    tooltip: label,
    iconClassName: "text-slate-500 dark:text-foreground-secondary",
  };
};
